/**
 * Shared Axios instance for all client-side API calls.
 * - Base URL is `/api` (proxied to the backend by Vite in dev, same-origin in prod).
 * - Request interceptor attaches the JWT from the auth store.
 * - Response interceptor unwraps errors into a normalized message and handles 401.
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { ApiError } from '@shared/types';

export const TOKEN_STORAGE_KEY = 'fb_ads_agent_token';

export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

/** Normalized error thrown to callers so UI code can show `err.message`. */
export class ApiRequestError extends Error {
  code: string;
  status?: number;
  details?: unknown;
  constructor(message: string, code: string, status?: number, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const status = error.response?.status;

    // Auto-logout on unauthorized (except during the login/register calls themselves).
    if (status === 401 && !error.config?.url?.includes('/auth/')) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }

    const payload = error.response?.data?.error;
    throw new ApiRequestError(
      payload?.message ?? error.message ?? 'Request failed',
      payload?.code ?? 'NETWORK_ERROR',
      status,
      payload?.details,
    );
  },
);
